import React from 'react';
import IdentityLink from './IdentityLink';
import { formatDate, formatDecimal, formatDocument } from './contract';
import driverPhoneColumn from './driverPhoneColumn';
import { placeLabel, titleCase, upperCase } from './text';
import {
  amountColumn,
  deadline,
  deadlineColumn,
  kmColumn,
  linkColumn,
  negotiatorColumn,
  payableColumn,
  pcpColumn,
  transitTimeColumn,
  updatedColumn,
  windowColumn,
} from './listColumns';

/** Catálogo das colunas de viagem, comum às abas por etapa e à Lista de viagens (5668–5671). */
export const missing = 'Não informado';
// 5674: textos como nos cards (5663): inicial maiúscula, UF com duas letras, razão social e placas
// em maiúsculas. Observações, posição e usuários ficam como vieram.
export const place = (point) => placeLabel(point) || missing;
const identifier = (value) => String(value ?? '').trim();

// Horários por ponto: com mais de um ponto, cada um aparece com o local.
function pointTimes(points, field) {
  if (!points?.length) return missing;
  return points
    .map((point) => `${points.length > 1 ? `${place(point)}: ` : ''}${formatDate(point[field])}`)
    .join(' · ');
}

/** Colunas que dependem do travel-progress: a lista precisa do TransitTimeProvider. */
export const PROGRESS_KEYS = [
  'lastPosition',
  'kmRemaining',
  'arrival',
  'responsible',
  'costCenter',
];

export function travelCatalog({ Occurrences, ProgressValue, integrationProps }) {
  // Coluna lida do travel-progress (posição, previsão, KM restante, responsável e CC).
  const progress = (key, label, field, width) => ({
    key,
    label,
    width,
    value: () => '',
    render: (row) => (ProgressValue ? <ProgressValue item={row} field={field} /> : missing),
  });
  return {
    position: {
      key: 'position',
      label: '#',
      width: 44,
      resizable: false,
      value: (_, i) => String(i + 1).padStart(2, '0'),
    },
    travel: {
      key: 'travel',
      label: 'Viagem',
      width: 115,
      value: (row) => String(row.travelId ?? row.id),
      render: (row, value) => (
        <IdentityLink
          to={`/viagens/${row.travelId ?? row.id}`}
          aria-label={`Abrir viagem ${row.travelId ?? row.id}`}
        >
          {value}
        </IdentityLink>
      ),
      addon: Occurrences ? (row) => <Occurrences {...integrationProps(row)} /> : undefined,
    },
    reference: {
      key: 'reference',
      label: 'Viagem Benner',
      width: 175,
      value: (row) => identifier(row.externalLoadId) || identifier(row.travelNumber) || missing,
    },
    status: {
      key: 'status',
      label: 'Status',
      width: 195,
      value: (row) => titleCase(row.status?.name) || row.status?.code || missing,
    },
    client: {
      key: 'client',
      label: 'Cliente',
      width: 220,
      value: (row) =>
        `${upperCase(row.client?.name) || 'Cliente não informado'} · ${formatDocument(row.client?.cnpj)}`,
    },
    product: {
      key: 'product',
      label: 'Produto',
      width: 175,
      value: (row) =>
        [
          titleCase(row.product?.name) || missing,
          row.quantity?.weight != null ? `${formatDecimal(row.quantity.weight)} kg` : null,
          row.quantity?.volume != null ? `Volume: ${row.quantity.volume}` : null,
          row.quantity?.pallets != null ? `Pallets: ${row.quantity.pallets}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
    },
    amount: amountColumn,
    carrier: {
      key: 'carrier',
      label: 'Transportador',
      width: 200,
      value: (row) => titleCase(row.carrier?.name) || missing,
    },
    driver: {
      key: 'driver',
      label: 'Motorista',
      width: 180,
      value: (row) => titleCase(row.driver?.name) || missing,
    },
    driverPhone: driverPhoneColumn(160),
    vehicle: {
      key: 'vehicle',
      label: 'Veículo',
      width: 200,
      value: (row) =>
        [
          upperCase(row.vehicle?.plate) || missing,
          ...(row.wagons || []).map((wagon) => upperCase(wagon.plate) || missing),
        ].join(' / '),
    },
    link: linkColumn,
    payable: payableColumn,
    origin: { key: 'origin', label: 'Origem', width: 165, value: (row) => place(row.origin) },
    destination: {
      key: 'destination',
      label: 'Destino',
      width: 165,
      value: (row) => place(row.destination),
    },
    km: kmColumn('km', 'KM Total', (row) => row.totalDistanceMeters),
    pickup: windowColumn(
      'pickup',
      'Coleta Agendada',
      (row) => row.scheduledPickupStartAt,
      (row) => row.scheduledPickupAt,
    ),
    delivery: windowColumn(
      'delivery',
      'Entrega Agendada',
      (row) => row.scheduledDeliveryStartAt,
      (row) => row.scheduledDeliveryAt,
    ),
    // Carregamento Iniciado/Finalizado: chegada e efetivação na origem (resposta do negócio, 5668).
    loadingStarted: {
      key: 'loadingStarted',
      label: 'Carregamento Iniciado',
      width: 185,
      value: (row) => pointTimes(row.origins, 'arrivedAt'),
    },
    loadingFinished: {
      key: 'loadingFinished',
      label: 'Carregamento Finalizado',
      width: 195,
      value: (row) => pointTimes(row.origins, 'completedAt'),
    },
    // Descarregamento Iniciado/Finalizado: chegada e efetivação no destino (5670).
    unloadingStarted: {
      key: 'unloadingStarted',
      label: 'Descarregamento Iniciado',
      width: 195,
      value: (row) => pointTimes(row.destinations, 'arrivedAt'),
    },
    unloadingFinished: {
      key: 'unloadingFinished',
      label: 'Descarregamento Finalizado',
      width: 205,
      value: (row) => pointTimes(row.destinations, 'completedAt'),
    },
    tracking: {
      key: 'tracking',
      label: 'Rastreado',
      width: 150,
      value: (row) =>
        [
          row.tracking?.required === true
            ? 'Sim'
            : row.tracking?.required === false
              ? 'Não'
              : missing,
          titleCase(row.tracking?.tracker?.name),
        ]
          .filter(Boolean)
          .join(' · '),
    },
    lastPosition: progress('lastPosition', 'Posição', 'position', 320),
    kmCovered: kmColumn('kmCovered', 'KM Percorrido', (row) => row.coveredDistanceMeters, 130),
    kmRemaining: progress('kmRemaining', 'KM Restante', 'remaining', 120),
    transitTime: transitTimeColumn,
    arrival: progress('arrival', 'Previsão De Chegada', 'arrival', 170),
    // 5710: uma linha por placa ("PLACA: …"); o CC ficou mais largo por causa da placa.
    responsible: progress('responsible', 'Responsável placa / filial', 'responsible', 360),
    costCenter: progress('costCenter', 'CC', 'costCenter', 240),
    // Prazo pela janela de descarga do destino (documento 1.6); a Lista de viagens usa a regra por status.
    deadline: deadlineColumn((row) => deadline(new Date().toISOString(), row.scheduledDeliveryAt)),
    observations: {
      key: 'observations',
      label: 'Observações',
      width: 235,
      value: (row) =>
        [row.observations?.comments, row.observations?.additionalInfo]
          .filter(Boolean)
          .join(' · ') || missing,
    },
    updated: updatedColumn,
    pcp: pcpColumn,
    negotiator: negotiatorColumn,
  };
}
